import React, { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, Kind } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BrandMark } from "@/components/Brand";
import { OtpStep } from "@/components/OtpStep";
import { GoogleButton, OrDivider } from "@/components/GoogleButton";
import { startGoogleLogin } from "@/pages/AuthCallback";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

type Audience = "vendor" | "attendee";

const PURPOSE: Record<Audience, Record<string, string>> = {
  vendor: { login: "vendor_login", signup: "vendor_signup" },
  attendee: { login: "customer_login", signup: "customer_signup" },
};

const COPY = {
  vendor: {
    login: "Vendor login", signup: "Start your free trial", tabs: ["Log in", "Create vendor account"],
    image: "/images/booth.jpg", alt: "Attendees at a vendor booth",
    quote: "“Fast capture, consent-proof sharing, and follow-up that actually happens.”",
    switchText: "Attending an event?", switchLabel: "Attendee login", switchTo: "/attendee",
  },
  attendee: {
    login: "Attendee login", signup: "Create your attendee account", tabs: ["Log in", "Create account"],
    image: "/images/wedding-expo.jpg", alt: "Couples browsing booths at a wedding expo",
    quote: "Sign up once before the show. Every booth QR you scan saves the vendor to your list instantly.",
    switchText: "Exhibiting at an event?", switchLabel: "Vendor login", switchTo: "/login",
  },
};

export default function AuthPage({ audience = "vendor" }: { audience?: Audience }) {
  const [params, setParams] = useSearchParams();
  const raw = params.get("mode");
  const mode = raw === "signup" ? "signup" : "login";
  const kind: Kind = audience === "vendor" ? "vendor" : "customer";
  const copy = COPY[audience];
  const nav = useNavigate();
  const { signIn } = useAuth();
  const [form, setForm] = useState({ email: "", company_name: "", name: "" });
  const [step, setStep] = useState<"email" | "otp">("email");
  const [busy, setBusy] = useState(false);

  const request = async () => {
    await api.post("/auth/otp/request", { ...form, purpose: PURPOSE[audience][mode] });
    toast.success("Code sent — check your inbox");
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await request();
      setStep("otp");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (code: string) => {
    try {
      const { data } = await api.post("/auth/otp/verify", { email: form.email, purpose: PURPOSE[audience][mode], code });
      await signIn(kind, data.token);
      nav(kind === "vendor" ? "/dashboard" : "/me");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const google = () => {
    if (audience === "vendor" && mode === "signup" && !form.company_name.trim()) return toast.error("Enter your company name first");
    startGoogleLogin(kind, kind === "vendor" ? "/dashboard" : "/me", audience === "vendor" && mode === "signup" ? form.company_name.trim() : undefined);
  };

  if (audience === "vendor" && raw === "customer") return <Navigate to="/attendee" replace />;

  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-2">
      <div className="flex flex-col px-6 py-6 sm:px-12">
        <BrandMark />
        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center gap-6 py-10">
          <div className="rise" data-testid={`auth-${audience}-page`}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">{audience === "vendor" ? "For vendors" : "For attendees"}</p>
            <h1 className="mt-1 text-3xl font-semibold tracking-tight" data-testid="auth-title">{copy[mode]}</h1>
            <p className="mt-2 text-sm text-muted-foreground">No passwords. We email you a one-time code.</p>
          </div>
          <Tabs value={mode} onValueChange={(v) => { setParams({ mode: v }); setStep("email"); }}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login" data-testid={`auth-${audience}-tab-login`}>{copy.tabs[0]}</TabsTrigger>
              <TabsTrigger value="signup" data-testid={`auth-${audience}-tab-signup`}>{copy.tabs[1]}</TabsTrigger>
            </TabsList>
          </Tabs>
          {step === "email" ? (
            <form onSubmit={submit} className="space-y-4 rise">
              {mode === "signup" && audience === "vendor" && (
                <div className="space-y-2">
                  <Label>Company name</Label>
                  <Input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="auth-company-input" />
                </div>
              )}
              {mode === "signup" && (
                <div className="space-y-2">
                  <Label>Your name</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="auth-name-input" />
                </div>
              )}
              <div className="space-y-2">
                <Label>Email</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-email-input" />
              </div>
              <Button type="submit" disabled={busy} className="hard-shadow h-12 w-full rounded-sm text-base" data-testid="auth-send-code-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Email me a code"}
              </Button>
              <OrDivider />
              <GoogleButton onClick={google} label={mode === "signup" ? "Sign up with Google" : "Continue with Google"} testid="auth-google-button" />
            </form>
          ) : (
            <OtpStep email={form.email} onVerify={verify} onResend={() => request().catch((e) => toast.error(errMsg(e)))} onBack={() => setStep("email")} />
          )}
          <p className="text-center text-sm text-muted-foreground">
            {copy.switchText}{" "}
            <Link to={copy.switchTo} className="font-semibold text-primary hover:underline" data-testid="auth-switch-audience-link">{copy.switchLabel}</Link>
          </p>
        </div>
      </div>
      <div className="relative hidden lg:block">
        <img src={copy.image} alt={copy.alt} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
        <p className="absolute bottom-10 left-10 right-10 text-2xl font-bold leading-snug text-white">
          {copy.quote}
        </p>
      </div>
    </div>
  );
}
